import { useNavigate, useSearchParams } from 'react-router';
import { DollarSign, Plus } from 'lucide-react';
import { TabsContent, Tabs } from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ShortcutButton } from '@/components/keyboard/ShortcutIndicator';
import { TransactionsTab } from '@/components/general-ledger';
import { DrawdownManagementView } from '@/components/drawdowns/DrawdownManagementView';
import { DistributionsList } from '@/components/distributions/DistributionsList';
import { TableSkeleton } from '@/components/ui/SuspenseBoundary';
import {
  useFundDistributions,
  useUpdateDistribution,
} from '@/hooks/react-query/useDistributions';
import { DistributionStatus } from '@/types/distribution';
import { FundFeesAndCostsTab as FeesAndCostsContent } from '@/pages/funds/FundFeesAndCostsTab';
import { FundCurrencyConversions } from '@/pages/funds/FundCurrencyConversions';
import type { Fund } from '@/types';
import { StatementImportPanel } from '@/components/bank-incoming';
import { FundConvertiblesTab } from './FundConvertiblesTab';
import { FundTransactionsList } from './FundTransactionsList';

interface FundTransactionsTabProps {
  fundId: string;
  fund: Fund;
  onInvestorCountAvailable?: (count: number) => void;
}

function DistributionsPlaceholder({
  isLoading,
  hasError,
}: {
  isLoading: boolean;
  hasError: boolean;
}) {
  if (isLoading) return <TableSkeleton rows={3} columns={4} />;
  if (hasError) {
    return (
      <div className="text-center py-12 text-destructive">
        <p>Failed to load distributions</p>
        <p className="text-sm mt-1">Please try again later</p>
      </div>
    );
  }
  return (
    <div className="text-center py-12 text-gray-500">
      <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
      <p>No distributions found</p>
      <p className="text-sm">Record your first distribution to get started</p>
    </div>
  );
}

export function FundTransactionsTab({
  fundId,
  fund,
  onInvestorCountAvailable,
}: FundTransactionsTabProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: distributionsData,
    isLoading: distributionsLoading,
    error: distributionsError,
  } = useFundDistributions(fundId);
  const updateDistribution = useUpdateDistribution();

  const distributions = distributionsData?.items ?? [];

  const handleCancelDistribution = (id: string) => {
    updateDistribution.mutate({
      fundId,
      distributionId: id,
      data: { status: DistributionStatus.CANCELLED },
    });
  };
  const activeSubTab = searchParams.get('section') || 'all';

  const setActiveSubTab = (value: string) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('section', value);
      newParams.delete('subsection');
      return newParams;
    });
  };

  return (
    <TabsContent value="transactions" className="flex-1 flex flex-col">
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsContent value="all">
          <FundTransactionsList fundId={fundId} />
        </TabsContent>

        <TabsContent value="drawdowns" className="mt-0">
          <DrawdownManagementView
            fundId={fundId}
            fund={fund}
            className="flex-1"
            onInvestorCountAvailable={onInvestorCountAvailable}
          />
        </TabsContent>

        <TabsContent value="gl-reference">
          <TransactionsTab fundId={fundId} />
        </TabsContent>

        <TabsContent value="distributions">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Distributions</CardTitle>
                  <CardDescription>
                    Track distributions and payments to investors
                  </CardDescription>
                </div>
                <ShortcutButton
                  shortcut={{ key: 'n' }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  onClick={() =>
                    navigate(`/app/funds/${fundId}/distributions/new`)
                  }
                >
                  <Plus className="h-4 w-4" />
                  New Distribution
                </ShortcutButton>
              </div>
            </CardHeader>
            <CardContent>
              {distributionsLoading ||
              distributionsError ||
              distributions.length === 0 ? (
                <DistributionsPlaceholder
                  isLoading={distributionsLoading}
                  hasError={Boolean(distributionsError)}
                />
              ) : (
                <DistributionsList
                  distributions={distributions}
                  onNavigate={(id) =>
                    navigate(`/app/funds/${fundId}/distributions/${id}`)
                  }
                  onCancel={handleCancelDistribution}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="convertibles" className="mt-1">
          <FundConvertiblesTab fundId={fundId} />
        </TabsContent>

        <TabsContent value="fees-and-costs" className="mt-1">
          <FeesAndCostsContent fund={fund} />
        </TabsContent>

        <TabsContent value="import-bank-transactions">
          <StatementImportPanel fundId={fundId} />
        </TabsContent>

        <TabsContent value="currency-conversions" className="mt-1">
          <FundCurrencyConversions
            fundId={fundId}
            fundCurrency={fund.base_currency}
          />
        </TabsContent>
      </Tabs>
    </TabsContent>
  );
}
